import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Zap, 
  Lightbulb, 
  Smartphone, 
  Home, 
  Wrench, 
  Building2,
  ArrowUpRight 
} from "lucide-react";

const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const services = [
    {
      icon: Zap,
      title: "Elinstallationer",
      description: "Professionella elinstallationer för hem och företag. Från enklare uppdrag till kompletta ombyggnationer.",
    },
    {
      icon: Lightbulb,
      title: "Ljusdesign för Villa",
      description: "Skräddarsydd ljussättning som förhöjer arkitekturen och skapar atmosfär i ditt hem.",
    },
    {
      icon: Smartphone,
      title: "Smart Belysning",
      description: "Intelligent styrning av belysning via app eller röst. Automatisera och optimera ditt ljus.",
    },
    {
      icon: Home,
      title: "Nyproduktion & Renovering",
      description: "Komplett elinstallation för nybygge eller totalrenovering. Vi är med från start till mål.",
    },
    {
      icon: Wrench,
      title: "Service & Felsökning",
      description: "Snabb och pålitlig service vid elfel. Vi diagnostiserar och åtgärdar alla typer av problem.",
    },
    {
      icon: Building2,
      title: "UE för Byggföretag",
      description: "Underentreprenör för byggprojekt i alla storlekar. Erfaren samarbetspartner.",
    },
  ];

  return (
    <section
      id="tjanster"
      data-testid="services-section"
      ref={ref}
      className="py-24 md:py-32 bg-[#0A0A0A]"
    >
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-2xl mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] mb-4"
          >
            Våra Tjänster
          </motion.p>

          <motion.h2
            data-testid="services-title"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-3xl md:text-4xl lg:text-5xl font-manrope font-light text-white tracking-tight"
          >
            Expertis inom
            <br />
            <span className="text-zinc-500">el och ljus.</span>
          </motion.h2>
        </div>

        {/* Services Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              data-testid={`service-card-${index}`}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
              className="service-card group relative p-8 bg-[#050505] border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500"
            >
              {/* Icon */}
              <div className="mb-6">
                <service.icon className="w-10 h-10 text-[#D4AF37] group-hover:scale-110 transition-transform duration-300" />
              </div>

              {/* Title */}
              <h3 className="text-xl font-manrope font-medium text-white mb-3 group-hover:text-[#D4AF37] transition-colors duration-300">
                {service.title}
              </h3>

              {/* Description */}
              <p className="text-zinc-500 text-sm leading-relaxed">
                {service.description}
              </p>

              {/* Hover Arrow */}
              <div className="absolute top-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ArrowUpRight className="w-5 h-5 text-[#D4AF37]" />
              </div>

              {/* Glow Effect on Hover */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
